import React, { useEffect, useMemo, useState } from "react";
import Form from "react-bootstrap/Form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import Button from "react-bootstrap/Button";
import "react-datepicker/dist/react-datepicker.css";
import {
  emptyString,
  translationConstants,
  modeConstants,
  levelConstants,
  studentConstants,
  Directions,
  sportConstants,
  fieldLengthConstants,
  levelEnum,
  levelEnumReversed,
  sportConstantsReversed,
  boatTypeEnumReversed,
} from "../../../constants";
import { ToastifyService } from "../../../services";
import { instructorActions, CourseActions } from "../../../redux/actions";
import {
  getSportDataForDropdown,
  getUserPayload,
  removeSpace,
} from "../../../system";

const CourseModal = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const userPayload = getUserPayload();

  const instructorId = useSelector(
    (state) => state.LoginReducer.UserData.instructorId
  );
  const allCenterInstructors = useSelector(
    (state) => state.InstructorReducer.allCenterInstructors
  );
  const direction = useSelector((state) => {
    return state.LanguageChangeReducer.direction;
  });

  const viewData = useSelector((state) => state.CourseReducer.viewData);
  const stats = useSelector((state) => state.CourseReducer.stats);
  const initialFormData = {
    studentName: "",
    studentArabicName: "",
    hoursSpend: 1,
    instructorId: instructorId,
    assignedLevelId: levelEnum.GENERAL_TRAINING,
    studentEmail: "",
    sportId: sportConstants.SAILING,
    comments: null,
    centerId: null,
  };

  const [selectedInstructor, setSelectedInstructor] = useState();
  const [formData, setFormData] = useState(initialFormData);
  const [isLevelCompleted, setIsLevelCompleted] = useState(true);

  // Memoize relevantSportsData so it only recalculates when its dependencies change
  const relevantSportsData = useMemo(() => {
    if (props?.isCenterAdmin) {
      return selectedInstructor?.instructorSports || [];
    }
    if (stats?.freelanceSports?.length > 0 || stats?.freelanceSports === null) {
      return stats.instructorSports?.filter((instructorSport) =>
        stats.freelanceSports.includes(instructorSport.sportId)
      );
    }
    return stats?.instructorSports || [];
  }, [
    props?.isCenterAdmin,
    selectedInstructor?.instructorSports,
    stats?.instructorSports,
    stats?.freelanceSports,
  ]);

  const [sportOptions, setSportOptions] = useState([]);
  const [boatTypeOptions, setBoatTypeOptions] = useState([]);
  const [levelOptions, setLevelOptions] = useState([]);
  const [proficiencyLevelOptions, setProficiencyLevelOptions] = useState([]);

  const [selectedSport, setSelectedSport] = useState("");

  const [isGeneralTraining, setGeneralTraining] = useState(false);
  const [searchInput, setSearchInput] = useState("");

  const {
    setValue,
    register,
    handleSubmit,
    formState: { errors },
    trigger,
    getValues,
  } = useForm();

  const onSubmit = (data) => {
    props.setShow(false);
    if (props.mode === modeConstants.ADD_MODE) {
      if (props?.isCenterAdmin) {
        dispatch(
          CourseActions.addStudent(formData, true, () => {
            dispatch(
              CourseActions.getStudentsByCenterId(
                props.centerGrid,
                userPayload?.user?.centerId
              )
            );
          })
        );
      } else {
        dispatch(CourseActions.addStudent(formData));
      }
    }
  };

  useEffect(() => {
    if (viewData !== null && Object.keys(viewData)?.length > 0) {
      setFormData((prevData) => ({
        ...prevData,
        studentName: viewData.studentName || emptyString,
        studentArabicName: viewData.studentArabicName || emptyString,
      }));
      viewData.studentName && setValue("studentName", viewData.studentName);
      viewData.studentArabicName &&
        setValue("studentArabicName", viewData.studentArabicName);
      trigger("studentName");
      trigger("studentArabicName");
      //=====================================================================================================

      //=====================================================================================================
      if (props.mode === modeConstants.VIEW_MODE) {
        // Set form values using setValue only if they are defined
        setIsLevelCompleted(viewData.comments ? false : true);
        viewData.sportId && setValue("sportId", viewData?.sportId);
        setBoatTypeOptions(getSportDataForDropdown(t, viewData.sportId));
        setLevelOptions(
          getSportDataForDropdown(t, viewData.sportId, viewData.boatTypeId)
        );
        setProficiencyLevelOptions(
          getSportDataForDropdown(
            t,
            viewData.sportId,
            viewData.boatTypeId,
            viewData.assignedLevelId
          )
        );
        setGeneralTraining(
          viewData.assignedLevelId === null && viewData.isGeneralTraining
        );
        viewData.hoursSpend && setValue("hoursSpend", viewData.hoursSpend);
        viewData.currentLevelId &&
          setValue(
            "currentLevelId",
            t(translationConstants[levelEnumReversed[viewData.currentLevelId]])
          );

        //Set initial form values from userData if they are defined
        setFormData((prevData) => ({
          ...prevData,
          id: viewData.id || emptyString,
          sportId: viewData.sportId || emptyString,
          hoursSpend: viewData.hoursSpend,
          currentLevelId: t(
            translationConstants[levelEnumReversed[viewData.currentLevelId]]
          ),
          studentEmail: viewData.studentEmail || emptyString,
          comments: viewData.comments || emptyString,
        }));
      }
    }
    // eslint-disable-next-line
  }, [viewData]);

  useEffect(() => {
    if (props.mode === modeConstants.VIEW_MODE) {
      viewData.boatTypeId && setValue("boatTypeId", viewData?.boatTypeId);
      setValue(
        "assignedLevelId",
        viewData.assignedLevelId === null && viewData.isGeneralTraining
          ? levelEnum.GENERAL_TRAINING
          : viewData.assignedLevelId
          ? viewData.assignedLevelId
          : emptyString
      );
      viewData.assignedProficiencyLevelId &&
        setValue(
          "assignedProficiencyLevelId",
          viewData?.assignedProficiencyLevelId
        );

      setFormData((prevData) => ({
        ...prevData,
        boatTypeId: viewData.boatTypeId || emptyString,
        assignedProficiencyLevelId:
          viewData.assignedProficiencyLevelId || emptyString,
        assignedLevelId:
          viewData.assignedLevelId === null && viewData.isGeneralTraining
            ? levelEnum.GENERAL_TRAINING
            : viewData.assignedLevelId
            ? viewData.assignedLevelId
            : emptyString,
      }));
    }
  }, [boatTypeOptions, levelOptions, proficiencyLevelOptions]);

  const handleSearch = () => {
    if (searchInput) {
      // Email is valid, perform the search operation
      dispatch(CourseActions.getStudentByEmail({ email: searchInput }));
    } else {
      ToastifyService.showError(t(translationConstants.SEARCH_FIELD_NOT_EMPTY));
    }
  };

  const handleSport = (boatTypeId) => {
    const id = Number(boatTypeId);
    if (viewData && Object.keys(viewData)?.length > 0) {
      const levelId = viewData?.levels?.find(
        (data) => data.boatTypeId === id
      )?.levelId;
      if (levelId) {
        setValue(
          "currentLevelId",
          t(translationConstants[levelEnumReversed[levelId]])
        );
        setFormData({
          ...formData,
          currentLevelId: t(translationConstants[levelEnumReversed[levelId]]),
        });
      } else {
        setFormData({
          ...formData,
          currentLevelId: "--",
        });
        setValue("currentLevelId", "--");
      }
    }
  };

  //Handle search input change
  const handleSearchChange = (email) => {
    // Regular expression for email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // Check if the email matches the regular expression
    if (emailRegex.test(email)) {
      setSearchInput(email);
    } else {
      setSearchInput("");
    }
  };

  useEffect(() => {
    if (props?.isCenterAdmin) {
      dispatch(
        instructorActions.getAllCenterInstructors(userPayload?.user?.centerId)
      );
    }
    setFormData(initialFormData);
    // eslint-disable-next-line
  }, []);

  // Populate unique sports for the sport dropdown
  useEffect(() => {
    const uniqueSports = [
      ...new Set(relevantSportsData?.map((item) => item.sportId)),
    ];

    setSportOptions(uniqueSports);
  }, [relevantSportsData]);

  // Handle sport change and update boat types
  const handleSportChange = (e) => {
    const sportId = parseInt(e?.target?.value ? e?.target?.value : e);
    setSelectedSport(sportId);

    // Filter boat types based on selected sport
    const filteredBoatTypes = relevantSportsData
      ?.filter((item) => item.sportId === sportId)
      .map((item) => ({
        value: item.boatTypeId,
        label: t(translationConstants[boatTypeEnumReversed[item.boatTypeId]]), // Assuming boatTypeEnumReversed maps the IDs to readable names
      }));

    setBoatTypeOptions([...new Set(filteredBoatTypes)]); // Unique boat types as objects with value and label
    setLevelOptions([]); // Reset level options
  };

  // Handle boat type change and update levels
  const handleBoatTypeChange = (e) => {
    const boatTypeId = parseInt(e?.target?.value ? e?.target?.value : e);

    // Filter max level based on selected sport and boat type
    const maxLevel = Math.max(
      ...relevantSportsData
        ?.filter(
          (item) =>
            item.sportId === selectedSport && item.boatTypeId === boatTypeId
        )
        .map((item) => item.levelId)
    );

    // Create level options as an array of objects with value and label (1 to maxLevel)
    const levels = Array.from({ length: maxLevel }, (_, i) => ({
      value: i + 1,
      label: t(translationConstants[levelEnumReversed[i + 1]]), // Assuming levelEnumReversed maps the IDs to readable names
    }));

    setLevelOptions(levels);
  };

  return (
    <section className="student-section">
      <div className="ViewHeader">
        <p className="viewUserPara">
          {props.mode === modeConstants.ADD_MODE
            ? t(translationConstants.CERTIFY_STUDENT)
            : t(translationConstants.VIEW_CERTIFY_STUDENT)}
        </p>
      </div>
      <p className="horizentalLine" />
      <div className="m-t-24">
        <Form
          noValidate
          className="ViewUserContent"
          onSubmit={() => handleSearch()}
        >
          <div className="inputRow">
            {props?.isCenterAdmin && (
              <Form.Group className="form-group-register">
                <Form.Label className="Form-labels">
                  {t(translationConstants.INSTRUCTOR_NAME)}:
                </Form.Label>
                <Form.Select
                  id="selectedInstructor"
                  {...register("selectedInstructor", {
                    required: t(translationConstants.INSTRUCTOR_REQUIRED),
                    validate: {
                      isNotEmpty: (value) =>
                        value !== "" ||
                        t(translationConstants.INVALID_INSTRUCTOR),
                    },
                  })}
                  onChange={(e) => {
                    const value = e.target.value;
                    setValue("selectedInstructor", value, {
                      shouldValidate: true,
                    });

                    setSportOptions([]);
                    setBoatTypeOptions([]);
                    setLevelOptions([]);
                    setProficiencyLevelOptions([]);

                    setSelectedInstructor(
                      allCenterInstructors?.data?.find((x) => x.id === value)
                    );
                    setFormData({
                      ...formData,
                      instructorId: value,
                      centerId: userPayload?.user?.centerId
                        ? userPayload?.user?.centerId
                        : null,
                    });
                    trigger("selectedInstructor");
                    e.stopPropagation();
                  }}
                  disabled={props.mode === modeConstants.VIEW_MODE}
                >
                  {props.mode === modeConstants.VIEW_MODE ? (
                    <option value="">
                      {direction === Directions.rtl &&
                      removeSpace(viewData?.instructorArabicName)
                        ? viewData?.instructorArabicName
                        : viewData?.instructorName}
                    </option>
                  ) : (
                    <option value="">
                      {t(translationConstants.SELECT_INSTRUCTOR)}
                    </option>
                  )}
                  {props.mode !== modeConstants.VIEW_MODE &&
                    allCenterInstructors?.data?.map((instructor) => (
                      <option
                        className="capitalize-first"
                        key={instructor?.id}
                        value={instructor?.id}
                      >
                        {direction === Directions.rtl &&
                        instructor?.arabicInstructorName
                          ? instructor?.arabicInstructorName
                          : instructor?.instructorName}
                      </option>
                    ))}
                </Form.Select>
                {errors?.selectedInstructor && (
                  <p className="errorText">
                    {errors.selectedInstructor.message}
                  </p>
                )}
              </Form.Group>
            )}

            <Form.Group className="form-group-register">
              <Form.Label className="Form-labels">
                {t(translationConstants.STUDENT_EMAIL)}:
              </Form.Label>
              <div
                className={`${
                  props.mode === modeConstants.ADD_MODE
                    ? "searchInputDivBGWhite"
                    : ""
                } d-flex justify-content-center align-items-center`}
              >
                <Form.Control
                  className="inputWithoutOutline"
                  {...register("studentEmail", {
                    required: t(translationConstants.STUDENT_EMAIL_IS_REQUIRED),
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: t(translationConstants.INVALID_STUDENT_EMAIL),
                    },
                  })}
                  value={formData?.studentEmail}
                  onChange={(e) => {
                    setValue("studentEmail", e.target.value, {
                      shouldValidate: true,
                    });
                    handleSearchChange(e.target.value);
                    trigger("studentEmail");
                    setFormData({
                      ...formData,
                      studentEmail: e.target.value,
                    });
                  }}
                  style={{ outline: "none !important" }}
                  id="studentEmail"
                  type="text"
                  maxLength={fieldLengthConstants.EMAIL_FIELD_LENGTH}
                  placeholder={t(translationConstants.STUDENT_EMAIL)}
                  disabled={props.mode === modeConstants.VIEW_MODE}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSearch(); // Call handleSearch function
                    }
                  }}
                />
                <div className="mt-1">
                  {props.mode === modeConstants.ADD_MODE ? (
                    <svg
                      onClick={handleSearch}
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{
                        left: direction === Directions.rtl ? "20px" : "unset",
                        right: direction === Directions.rtl ? "unset" : "20px",
                      }}
                    >
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M8.15336 7.34119L11.2153 10.4032C11.323 10.5109 11.3834 10.6569 11.3833 10.8092C11.3833 10.9614 11.3227 11.1074 11.215 11.215C11.1073 11.3227 10.9613 11.3831 10.809 11.383C10.6568 11.383 10.5108 11.3225 10.4032 11.2148L7.34119 8.15279C6.42585 8.86175 5.2748 9.19538 4.12221 9.0858C2.96961 8.97623 1.90205 8.43168 1.1367 7.56293C0.371351 6.69419 -0.0342993 5.56651 0.00227447 4.40929C0.0388482 3.25208 0.514898 2.15226 1.33358 1.33358C2.15226 0.514898 3.25208 0.0388482 4.40929 0.00227447C5.56651 -0.0342993 6.69419 0.37135 7.56294 1.1367C8.43168 1.90205 8.97623 2.96961 9.08581 4.12221C9.19538 5.2748 8.86232 6.42585 8.15336 7.34119ZM4.55354 7.96781C5.45922 7.96781 6.32779 7.60804 6.9682 6.96763C7.60861 6.32722 7.96839 5.45865 7.96839 4.55297C7.96839 3.6473 7.60861 2.77872 6.9682 2.13832C6.32779 1.49791 5.45922 1.13813 4.55354 1.13813C3.64787 1.13813 2.77929 1.49791 2.13889 2.13832C1.49848 2.77872 1.1387 3.6473 1.1387 4.55297C1.1387 5.45865 1.49848 6.32722 2.13889 6.96763C2.77929 7.60804 3.64787 7.96781 4.55354 7.96781Z"
                        fill="#2C7465"
                      />
                    </svg>
                  ) : (
                    ""
                  )}
                </div>
              </div>
              {errors.studentEmail && (
                <p className="errorText">{errors.studentEmail.message}</p>
              )}
            </Form.Group>

            <Form.Group className="form-group-register">
              <Form.Label className="Form-labels">
                {t(translationConstants.CURRENT_LEVEL)}:
              </Form.Label>
              <Form.Control
                {...register("currentLevelId", {
                  required: t(translationConstants.CURRENT_LEVEL_REQUIRED),
                })}
                value={formData?.currentLevelId}
                onChange={(e) => {
                  setValue("currentLevelId", e.target.value, {
                    shouldValidate: true,
                  });
                  trigger("currentLevelId");
                  setFormData({
                    ...formData,
                    currentLevelId: e.target.value,
                  });
                }}
                id="currentLevelId"
                type="text"
                disabled={true}
              />
              {errors.currentLevelId && (
                <p className="errorText">{errors.currentLevelId.message}</p>
              )}
            </Form.Group>

            <Form.Group className="form-group-register">
              <Form.Label className="Form-labels">
                {t(translationConstants.STUDENT_NAME)}:
              </Form.Label>
              <Form.Control
                {...register("studentName", {
                  required: t(translationConstants.NAME_REQUIRED),
                  pattern: {
                    value: /^[A-Za-z\s]+$/,
                    message: t(translationConstants.INVALID_NAME),
                  },
                })}
                value={formData?.studentName}
                onChange={(e) => {
                  setValue("studentName", e.target.value, {
                    shouldValidate: true,
                  });
                  trigger("studentName");
                  setFormData({
                    ...formData,
                    studentName: e.target.value,
                  });
                }}
                id="studentName"
                maxLength={fieldLengthConstants.TEXT_FIELD_LENGTH}
                type="text"
                placeholder={t(translationConstants.ENTER_STUDENT_NAME)}
                disabled={props.mode === modeConstants.VIEW_MODE}
              />
              {errors.studentName && (
                <p className="errorText">{errors.studentName.message}</p>
              )}
            </Form.Group>

            <Form.Group className="form-group-register">
              <Form.Label className="Form-labels">
                {t(translationConstants.STUDENT_NAME_ARABIC)}:
              </Form.Label>
              <Form.Control
                {...register("studentArabicName", {})}
                dir={Directions.rtl}
                value={formData?.studentArabicName}
                onChange={(e) => {
                  setValue("studentArabicName", e.target.value, {
                    shouldValidate: true,
                  });
                  trigger("studentArabicName");
                  setFormData({
                    ...formData,
                    studentArabicName: e.target.value,
                  });
                }}
                id="studentArabicName"
                type="text"
                maxLength={fieldLengthConstants.TEXT_FIELD_LENGTH}
                placeholder={t(translationConstants.ENTER_ARABIC_NAME)}
                disabled={props.mode === modeConstants.VIEW_MODE}
              />
              {errors.studentArabicName && (
                <p className="errorText">{errors.studentArabicName.message}</p>
              )}
            </Form.Group>

            <Form.Group className="form-group-register">
              <Form.Label className="Form-labels">
                {t(translationConstants.COURSE_HOURS)}:
              </Form.Label>
              <Form.Control
                {...register("hoursSpend", {
                  required: t(translationConstants.COURSE_HOURS_REQUIRED),
                  pattern: {
                    value: /^[1-9][0-9]*$/,
                    message: t(translationConstants.INVALID_HOURS),
                  },
                  maxLength: {
                    value: 16,
                    message: t(translationConstants.MAX_HOURS_LENGHT_INVALID),
                  },
                })}
                value={formData?.hoursSpend}
                onChange={(e) => {
                  setValue("hoursSpend", e.target.value, {
                    shouldValidate: true,
                  });
                  trigger("hoursSpend");
                  setFormData({
                    ...formData,
                    hoursSpend: e.target.value,
                  });
                }}
                id="hoursSpend"
                type="text"
                placeholder={t(translationConstants.ENTER_COURSE_HOURS)}
                disabled={props.mode === modeConstants.VIEW_MODE}
              />
              {errors.hoursSpend && (
                <p className="errorText">{errors.hoursSpend.message}</p>
              )}
            </Form.Group>

            <Form.Group className="form-group-register">
              <Form.Label className="Form-labels">
                {t(translationConstants.SPORT_TYPE)}:
              </Form.Label>
              <Form.Select
                id="sportId"
                {...register("sportId", {
                  required: t(translationConstants.SPORT_TYPE_IS_REQUIRED),
                  validate: {
                    isNotEmpty: (value) =>
                      value !== "" ||
                      t(translationConstants.INVALID_SPORT_TYPE),
                  },
                })}
                selected={formData?.sportId}
                onChange={(e) => {
                  const value = e.target.value;
                  handleSportChange(e);
                  setValue("sportId", value, { shouldValidate: true });
                  setFormData({
                    ...formData,
                    sportId: e.target.value,
                  });
                  trigger("sportId");
                  e.stopPropagation();
                }}
                aria-label="Default select example"
                disabled={props.mode === modeConstants.VIEW_MODE}
              >
                <option value="">{t(translationConstants.Select)}</option>
                {sportOptions?.length > 0
                  ? sportOptions?.map((id, index) => {
                      return (
                        <>
                          <option key={index} value={id}>
                            {t(
                              translationConstants[sportConstantsReversed[id]]
                            )}
                          </option>
                        </>
                      );
                    })
                  : viewData?.sportId && (
                      <>
                        <option key={viewData.sportId} value={viewData.sportId}>
                          {t(
                            translationConstants[
                              sportConstantsReversed[viewData.sportId]
                            ]
                          )}
                        </option>
                      </>
                    )}
              </Form.Select>
              {errors.sportId && (
                <p className="errorText">{errors.sportId.message}</p>
              )}
            </Form.Group>

            <Form.Group className="form-group-register">
              <Form.Label className="Form-labels">
                {t(translationConstants.BOAT_TYPE)}:
              </Form.Label>
              <Form.Select
                id="boatTypeId"
                {...register("boatTypeId", {
                  required: t(translationConstants.BOAT_TYPE_REQUIRED),
                  validate: {
                    isNotEmpty: (value) =>
                      value !== "" || t(translationConstants.INVALID_BOAT_TYPE),
                  },
                })}
                selected={formData?.boatTypeId}
                onChange={(e) => {
                  const value = e.target.value;
                  handleBoatTypeChange(e);
                  setValue("boatTypeId", value, { shouldValidate: true });
                  handleSport(e.target.value);
                  setFormData({
                    ...formData,
                    boatTypeId: e.target.value,
                  });
                  trigger("boatTypeId");
                  e.stopPropagation();
                }}
                aria-label="Default select example"
                disabled={props.mode === modeConstants.VIEW_MODE}
              >
                <option value="">{t(translationConstants.Select)}</option>
                {boatTypeOptions?.map((object) => (
                  <option key={object.value} value={object.value}>
                    {t(
                      translationConstants[boatTypeEnumReversed[object.value]]
                    )}
                  </option>
                ))}
              </Form.Select>
              {errors.boatTypeId && (
                <p className="errorText">{errors.boatTypeId.message}</p>
              )}
            </Form.Group>

            <Form.Group className="form-group-register">
              <Form.Label className="Form-labels">
                {t(translationConstants.LEVEL_COMPLETED)}:
              </Form.Label>
              <div className="d-flex mt-0 gap-4">
                <Form.Check
                  className="custom-checkbox custom-checkboxInput"
                  type="radio"
                  label={t(translationConstants.YES)}
                  name="radioGroup"
                  id="yes"
                  checked={viewData?.comments ? false : isLevelCompleted}
                  onChange={(e) => {
                    setIsLevelCompleted(true);
                    setValue("comments", null);
                  }}
                  disabled={props.mode === modeConstants.VIEW_MODE}
                />
                <Form.Check
                  className="custom-checkbox custom-checkboxInput"
                  type="radio"
                  label={t(translationConstants.NO)}
                  name="radioGroup"
                  id="no"
                  checked={
                    viewData?.comments ? true : isLevelCompleted ? false : true
                  }
                  onChange={(e) => {
                    setIsLevelCompleted(false);
                    setValue("assignedLevelId", null);
                    setValue("assignedProficiencyLevelId", null);
                    setFormData({
                      ...formData,
                      isGeneralTraining: false,
                      assignedLevelId: null,
                      assignedProficiencyLevelId: null,
                    });
                  }}
                  disabled={props.mode === modeConstants.VIEW_MODE}
                />
              </div>
            </Form.Group>

            {isLevelCompleted ? (
              <>
                <Form.Group className="form-group-register">
                  <Form.Label className="Form-labels">
                    {t(translationConstants.LEVEL_ASSIGNED)}:
                  </Form.Label>
                  <Form.Select
                    id="assignedLevelId"
                    {...register("assignedLevelId", {
                      required: t(
                        translationConstants.ASSIGNED_LEVEL_IS_REQUIRED
                      ),
                      validate: {
                        isNotEmpty: (value) =>
                          value !== "" || t(translationConstants.INVALID_LEVEL),
                      },
                    })}
                    onChange={(e) => {
                      const value = e.target.value;
                      setProficiencyLevelOptions(
                        getSportDataForDropdown(
                          t,
                          getValues("sportId"),
                          getValues("boatTypeId"),
                          e.target.value
                        )
                      );
                      if (e.target.value == levelEnum.GENERAL_TRAINING) {
                        setGeneralTraining(true);
                        setValue("assignedProficiencyLevelId", null);
                      } else {
                        setGeneralTraining(false);
                      }
                      setValue("assignedLevelId", value, {
                        shouldValidate: true,
                      });
                      setFormData({
                        ...formData,
                        isGeneralTraining:
                          e.target.value == levelEnum.GENERAL_TRAINING
                            ? true
                            : false,
                        assignedLevelId:
                          e.target.value == levelEnum.GENERAL_TRAINING
                            ? null
                            : e.target.value,
                        ...(e.target.value == levelEnum.GENERAL_TRAINING && {
                          assignedProficiencyLevelId: null,
                        }),
                      });
                      trigger("assignedLevelId");
                      e.stopPropagation();
                    }}
                    aria-label="Default select example"
                    disabled={props.mode === modeConstants.VIEW_MODE}
                  >
                    <option value="">{t(translationConstants.Select)}</option>
                    {levelOptions?.length > 0 && (
                      <option value={levelEnum.GENERAL_TRAINING}>
                        {t(translationConstants.GENERAL_TRAINING)}
                      </option>
                    )}
                    {levelOptions.map((object) => (
                      <option key={object.value} value={object.value}>
                        {t(
                          translationConstants[levelEnumReversed[object.value]]
                        )}
                      </option>
                    ))}
                  </Form.Select>
                  {errors.assignedLevelId && (
                    <p className="errorText">
                      {errors.assignedLevelId.message}
                    </p>
                  )}
                </Form.Group>

                {!isGeneralTraining && (
                  <Form.Group className="form-group-register">
                    <Form.Label className="Form-labels">
                      {t(translationConstants.PROFICIENCY_LEVEL_ASSIGNED)}:
                    </Form.Label>
                    <Form.Select
                      id="assignedProficiencyLevelId"
                      {...register("assignedProficiencyLevelId", {
                        required: t(
                          translationConstants.ASSIGNED_PROFICIENCY_LEVEL_IS_REQUIRED
                        ),
                        validate: {
                          isNotEmpty: (value) =>
                            value !== "" ||
                            t(translationConstants.INVALID_PROFICIENCY_LEVEL),
                        },
                      })}
                      onChange={(e) => {
                        const value = e.target.value;
                        setValue("assignedProficiencyLevelId", value, {
                          shouldValidate: true,
                        });
                        setFormData({
                          ...formData,
                          assignedProficiencyLevelId: e.target.value,
                        });
                        trigger("assignedProficiencyLevelId");
                        e.stopPropagation();
                      }}
                      aria-label="Default select example"
                      disabled={props.mode === modeConstants.VIEW_MODE}
                    >
                      <option value="">{t(translationConstants.Select)}</option>
                      {proficiencyLevelOptions.map((object) => (
                        <option key={object.value} value={object.value}>
                          {t(translationConstants[object.label])}
                        </option>
                      ))}
                    </Form.Select>
                    {errors.assignedProficiencyLevelId && (
                      <p className="errorText">
                        {errors.assignedProficiencyLevelId.message}
                      </p>
                    )}
                  </Form.Group>
                )}
              </>
            ) : (
              ""
            )}
          </div>

          {!isLevelCompleted || viewData?.comments ? (
            <Form.Group className="form-group-register">
              <Form.Label className="Form-labels">
                {t(translationConstants.REASON)}:
              </Form.Label>
              <Form.Control
                as="textarea" // Set the "as" prop to "textarea"
                {...register("comments", {
                  required: t(translationConstants.REASON_IS_REQUIRED),
                })}
                value={formData?.comments}
                onChange={(e) => {
                  setValue("comments", e.target.value, {
                    shouldValidate: true,
                  });
                  trigger("comments");
                  setFormData({
                    ...formData,
                    comments: e.target.value,
                  });
                }}
                id="comments"
                placeholder={t(translationConstants.ENTER_REASON)}
                disabled={props.mode === modeConstants.VIEW_MODE}
              />
              {errors.comments && (
                <p className="errorText">{errors.comments.message}</p>
              )}
            </Form.Group>
          ) : (
            ""
          )}

          {props.mode === modeConstants.ADD_MODE ? (
            <Button
              className="SubmitDocs-button"
              type=" button"
              onClick={handleSubmit(onSubmit)}
            >
              {t(translationConstants.submit)}
            </Button>
          ) : (
            ""
          )}
        </Form>
      </div>
    </section>
  );
};

export default CourseModal;
